import React, {useCallback, useMemo, useRef} from 'react';
import {
  DropTargetLayout,
  DropTargetOffsets,
  Layout,
  Spacer,
  Text,
  useTheme,
} from '@di-nxt/components';
import {TaskElement} from './TaskElement';
import {ShiftProps} from './types';
import {
  formatDuration,
  ScheduleCalendarType,
  Task,
  useScheduleDataContext,
} from '../../services';
import {getDropSequenceNo} from './functions/getDropSequenceNo';
import dayjs from 'dayjs';
import {assert} from 'assert-ts';

export const Shift: React.FC<ShiftProps> = ({kind, shiftWithTasks}) => {
  const theme = useTheme();
  const {
    shift: {durationMinutes: shiftDurationMinutes, id},
    totalTasksHours,
    tasks,
  } = shiftWithTasks;
  const context = useScheduleDataContext();

  const editedSequenceNoRef = useRef<number>();
  const totalTaskDuration = useMemo(
    () => tasks.reduce((acc, c) => acc + (c.durationMinutesEstimated ?? 0), 0),
    [tasks],
  );

  const handleTaskBeginHover = useCallback(
    (task: Task, offsets: DropTargetOffsets) => {
      const taskSequence = getDropSequenceNo(
        task,
        totalTaskDuration,
        tasks,
        offsets,
      );
      editedSequenceNoRef.current = taskSequence;
      context.beginHoverTaskOverShift(task.id, id, taskSequence);
    },
    [context, id, totalTaskDuration, tasks],
  );

  const handleTaskHover = useCallback(
    (task: Task, offsets: DropTargetOffsets) => {
      const taskSequence = getDropSequenceNo(
        task,
        totalTaskDuration,
        tasks,
        offsets,
      );

      if (taskSequence !== editedSequenceNoRef.current) {
        context.hoverTaskOverShift(task.id, id, taskSequence);
        editedSequenceNoRef.current = taskSequence;
      }
    },
    [context, id, totalTaskDuration, tasks],
  );

  const handleTaskEndHover = useCallback(
    (task: Task) => {
      editedSequenceNoRef.current = undefined;

      if (
        assert.soft(task, 'Shift:handleTaskEndHover: Task expected', {
          task,
          shiftId: id,
        })
      ) {
        context.endHoverTaskOverShift(task.id, id);
      }
    },
    [context, id],
  );

  const handleTaskDrop = useCallback(
    (task: Task, offsets: DropTargetOffsets) => {
      const taskSequence = getDropSequenceNo(
        task,
        totalTaskDuration,
        tasks,
        offsets,
      );
      context.dropTaskOnShift(task.id, id, taskSequence);
    },
    [context, id, totalTaskDuration, tasks],
  );

  // Make sure we only create drop targets for shifts that is within
  // the selected date when date kind is day, and that we don't allow
  // dropping on shifts that are not active (covered by absences).
  const isDropEnabled = useMemo(() => {
    return (
      kind === 'day' &&
      dayjs(context.date).isSame(
        dayjs(shiftWithTasks.shift.distrDate),
        'day',
      ) &&
      shiftWithTasks.active
    );
  }, [
    context.date,
    kind,
    shiftWithTasks.active,
    shiftWithTasks.shift.distrDate,
  ]);

  const overtime = totalTasksHours * 60 > shiftDurationMinutes;
  return kind === 'month' ? (
    // Month
    <Layout centered width={'100%'} opacity={shiftWithTasks.active ? 1 : 0.6}>
      <Layout
        backgroundColor={
          shiftWithTasks.tasks.length > 0
            ? 'background_location_008'
            : 'background_mono_tertiary'
        }
        borderStyle={shiftWithTasks.active ? undefined : 'solid'}
        borderColor={shiftWithTasks.active ? undefined : 'border_mono_15'}
        borderWidth={shiftWithTasks.active ? undefined : '1'}
        borderPosition="inside"
        borderRadius="9999"
        height={styleMap[kind].height}
        width={styleMap[kind].height}
      />
    </Layout>
  ) : kind === 'week' ? (
    // Week
    <Layout
      wrap="nowrap"
      opacity={shiftWithTasks.active ? 1 : 0.4}
      height={styleMap[kind].height}
      overflow="hidden"
      horizontal>
      <Layout
        horizontal
        absolute
        left={4}
        right={4}
        backgroundColor={'background_mono_tertiary'}
        borderRadius={'8'}>
        <Layout absolute zIndex={4} align="center" paddingLeft="4">
          <Text
            variant="inline_12_emphasis"
            textColor={'content_mono_secondary'}>
            {formatDuration(shiftDurationMinutes, true)}
          </Text>
        </Layout>
        {tasks.map((task, idx) => {
          return (
            <React.Fragment key={task.id}>
              <TaskElement
                durationAmount={
                  task.durationMinutesEstimated
                    ? task.durationMinutesEstimated / shiftDurationMinutes
                    : 0
                }
                kind={kind}
                task={task}
              />
              {idx < tasks.length - 1 && <Spacer width="2" />}
            </React.Fragment>
          );
        })}
      </Layout>
    </Layout>
  ) : (
    // Day
    <DropTargetLayout
      type={'Task'}
      enabled={isDropEnabled}
      flex
      vertical
      opacity={shiftWithTasks.active ? 1 : 0.4}
      onBeginHover={handleTaskBeginHover}
      onHover={handleTaskHover}
      onEndHover={handleTaskEndHover}
      onDrop={handleTaskDrop}>
      {/** Since text overflow hidden is not working with flex sized elements,
       * we need to wrap the text in a container with the height of the text
       * and then render the text with absolute position inside.
       */}
      <Layout
        paddingLeft="2"
        paddingVertical="2"
        height={theme.typography['inline_12_emphasis'].fontSizePx + 4}>
        {overtime ? (
          <Layout absolute align="center">
            <Text
              nowrap
              variant="inline_12_emphasis"
              textColor={'content_negative_default'}>
              {formatDuration(totalTasksHours * 60, true)}
            </Text>
            <Text
              nowrap
              variant="inline_12_default"
              textColor={'content_mono_secondary'}>
              {'/' + formatDuration(shiftDurationMinutes, true)}
            </Text>
          </Layout>
        ) : (
          <Layout absolute>
            <Text variant="inline_12_default" nowrap>
              {formatDuration(shiftDurationMinutes, true)}
            </Text>
          </Layout>
        )}
      </Layout>
      <Layout
        backgroundColor={'background_mono_tertiary'}
        borderStyle={shiftWithTasks.active ? undefined : 'solid'}
        borderColor={shiftWithTasks.active ? undefined : 'border_mono_15'}
        borderWidth={shiftWithTasks.active ? undefined : '1'}
        borderPosition="inside"
        borderRadius={'8'}
        height={styleMap[kind].height}
        direction="Horizontal">
        {tasks.map((task, idx) => {
          return (
            <React.Fragment key={`${task.id}-${idx}`}>
              <TaskElement
                durationAmount={
                  task.durationMinutesEstimated
                    ? task.durationMinutesEstimated / shiftDurationMinutes
                    : 0
                }
                kind={'day'}
                task={task}
              />
              {idx < tasks.length - 1 && <Spacer width="2" />}
            </React.Fragment>
          );
        })}
        {overtime ? (
          <Layout absolute pointerEvents="none">
            <Layout flex={shiftDurationMinutes / 60} />
            <Layout
              borderPosition="inside"
              borderTopRightRadius={'8'}
              borderTopColor={'border_negative_default'}
              borderTopWidth={'2'}
              borderTopStyle={'solid'}
              borderBottomRightRadius={'8'}
              borderBottomColor={'border_negative_default'}
              borderBottomWidth={'2'}
              borderBottomStyle={'solid'}
              borderRightRadius={'8'}
              borderRightColor={'border_negative_default'}
              borderRightWidth={'2'}
              borderRightStyle={'solid'}
              overflow="hidden"
              flex={Math.max(0, totalTasksHours - shiftDurationMinutes / 60)}>
              <Layout
                absolute
                opacity={0.6}
                backgroundColor="background_negative_light"
              />
            </Layout>
          </Layout>
        ) : null}
      </Layout>
    </DropTargetLayout>
  );
};

const styleMap: {[key in ScheduleCalendarType]: {height: number}} = {
  day: {
    height: 36,
  },
  week: {
    height: 24,
  },
  month: {
    height: 10,
  },
};
